import { useState } from "react";
import type { CedenteDoSistema } from "@/api/contrato";
import { useSessao } from "@/sessao/useSessao";
import { useAlterarVinculo, usePaginaDeConvenios } from "@/api/consultas";
import { Campo } from "@/componentes/Campo";
import { Esqueleto } from "@/componentes/Esqueleto";
import { ModalDeCadastro } from "@/componentes/ModalDeCadastro";
import { useAvisos } from "@/componentes/Avisos";
import { textoDaRecusa } from "@/telas/cedentes/recusas";
import { EscolhaDeConvenios } from "./EscolhaDeConvenios";

/*
 * As duas edições do vínculo que pedem formulário: convênios permitidos e teto
 * próprio. Suspender e reativar não têm campo — são confirmação, na aba.
 *
 * As duas abrem pela aba Cedentes da aplicação e pelo painel do vínculo, na página do
 * cedente, e a recusa sai em vocabulário de tela: o `detail` de
 * `vinculo_anterior_irrestrito` cita rota e `convenios_permitidos`.
 */

/** O que a edição lê do vínculo, nas duas telas que a abrem. */
export type VinculoEditavel = Pick<
  CedenteDoSistema,
  "pessoa_id" | "razao_social" | "convenios_permitidos" | "limite_mensal_override"
>;

export function ConveniosDoVinculo({
  sistemaID,
  cedente,
  aoFechar,
}: {
  sistemaID: string;
  cedente: VinculoEditavel;
  aoFechar: () => void;
}) {
  const { cliente } = useSessao();
  const avisos = useAvisos();
  // O filtro é da API: filtrar aqui olharia só os 200 primeiros convênios de todos os
  // cedentes, e o convênio deste poderia não estar entre eles.
  const convenios = usePaginaDeConvenios(cliente, { pessoa_id: cedente.pessoa_id }, true);
  const alterar = useAlterarVinculo(cliente, sistemaID, cedente.pessoa_id);

  const [permitidos, setPermitidos] = useState<string[] | null>(cedente.convenios_permitidos);
  const [erro, setErro] = useState<string | undefined>(undefined);

  function enviar() {
    // Sem a lista à vista, o operador estaria salvando o que não viu.
    if (convenios.data === undefined) return;

    setErro(undefined);
    alterar.mutate(
      // O campo vai sempre: ausente é recusado com `422`, porque um corpo que o
      // esquecesse alargaria o vínculo em silêncio.
      { tipo: "convenios", corpo: { convenios_permitidos: permitidos } },
      {
        onSuccess: () => {
          avisos.feito("Convênios definidos");
          aoFechar();
        },
        onError: (falha) => setErro(textoDaRecusa(falha)),
      },
    );
  }

  return (
    <ModalDeCadastro
      aberto
      aoFechar={aoFechar}
      titulo="Convênios permitidos"
      descricao={cedente.razao_social}
      aoEnviar={enviar}
      enviando={alterar.isPending}
      erro={erro ?? (convenios.error === null ? undefined : textoDaRecusa(convenios.error))}
      rotuloDeEnvio="Salvar"
    >
      {/*
        Sem os dados, "Nenhum convênio." seria falso: carregando, ou com a leitura
        recusada, a escolha ainda não existe.
      */}
      {convenios.data !== undefined ? (
        <EscolhaDeConvenios
          convenios={convenios.data.convenios}
          valor={permitidos}
          aoMudar={setPermitidos}
        />
      ) : (
        convenios.error === null && <Esqueleto className="h-24" />
      )}
    </ModalDeCadastro>
  );
}

const MAXIMO_INTEIRO = 2_147_483_647;

export function TetoDoVinculo({
  sistemaID,
  cedente,
  aoFechar,
}: {
  sistemaID: string;
  cedente: VinculoEditavel;
  aoFechar: () => void;
}) {
  const { cliente } = useSessao();
  const avisos = useAvisos();
  const alterar = useAlterarVinculo(cliente, sistemaID, cedente.pessoa_id);

  const [texto, setTexto] = useState(
    cedente.limite_mensal_override === null ? "" : String(cedente.limite_mensal_override),
  );
  const [erroDoCampo, setErroDoCampo] = useState<string | undefined>(undefined);
  const [erro, setErro] = useState<string | undefined>(undefined);

  function enviar() {
    setErro(undefined);
    const valor = texto.trim();
    // Vazio é SEM teto próprio (`null`), e nunca zero: zero é um cedente que
    // não emite nada.
    if (valor !== "" && !/^\d+$/.test(valor)) {
      setErroDoCampo("Use um número inteiro.");
      return;
    }
    const limite = valor === "" ? null : Number(valor);
    if (limite !== null && limite > MAXIMO_INTEIRO) {
      setErroDoCampo("Valor acima do máximo.");
      return;
    }
    setErroDoCampo(undefined);

    alterar.mutate(
      { tipo: "limite", corpo: { limite_mensal_override: limite } },
      {
        onSuccess: () => {
          avisos.feito(limite === null ? "Teto próprio removido" : "Teto próprio definido");
          aoFechar();
        },
        onError: (falha) => setErro(textoDaRecusa(falha)),
      },
    );
  }

  return (
    <ModalDeCadastro
      aberto
      aoFechar={aoFechar}
      titulo="Teto próprio"
      descricao={cedente.razao_social}
      aoEnviar={enviar}
      enviando={alterar.isPending}
      erro={erro}
      rotuloDeEnvio="Salvar"
    >
      <Campo
        rotulo="Boletos por mês"
        inputMode="numeric"
        dica="Vazio: sem teto próprio. Só restringe — nunca amplia o teto da aplicação nem o do contrato do cedente."
        value={texto}
        onChange={(evento) => setTexto(evento.target.value)}
        erro={erroDoCampo}
        className="w-40"
        autoFocus
      />
    </ModalDeCadastro>
  );
}
